import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { applicationActions } from "store/sagas/application/application.actions";
import { roomsActions } from "store/sagas/rooms/rooms.actions";
import { datesStateSelectors } from "store/slices/dates/selector";
import { filtersStateSelectors } from "store/slices/filters/selector";
import { roomsSelectors } from "store/slices/rooms/selectors";
import { useAppSelector } from "store/store.hooks";
import { RoomEntity } from "types/rooms";

const useRooms = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoadingRooms = useAppSelector(roomsSelectors.selectIsLoadingRooms);
  const allRooms = useAppSelector(roomsSelectors.selectRooms);
  const paginationInfo = useAppSelector(roomsSelectors.selectPaginationInfo);
  const dates = useAppSelector(datesStateSelectors.startEndDates);
  const filters = useAppSelector(filtersStateSelectors.activeFilters);
  const handleGoToRoom = (roomID: string) => {
    navigate(roomID);
  };
  const hasNextPage = paginationInfo?.hasNextPage;
  const handleSaveQuote = (room: RoomEntity) => {
    dispatch(
      applicationActions.saveQuoteRequested({
        spaceId: room.id,
        checkIn: dates.startDate,
        checkOut: dates.endDate,
      }),
    );
  };
  // REGION: BE Pagination
  const handleNextPage = () => {
    if (!paginationInfo?.hasNextPage || !paginationInfo?.nextPage) return;
    dispatch(
      roomsActions.getRoomsByProjectRequested({
        dates,
        filters,
        pagination: { page: paginationInfo.nextPage, perPage: 10 },
      }),
    );
  };
  const handlePrevPage = () => {
    if (!paginationInfo?.hasPrevPage || !paginationInfo?.prevPage) return;
    dispatch(
      roomsActions.getRoomsByProjectRequested({
        dates,
        filters,
        pagination: { page: paginationInfo.prevPage, perPage: 10 },
      }),
    );
  };
  // ENDREGION: Pagination

  // REGION: Local Pagination
  const [localPage, setLocalPage] = useState(1);
  const localPerPage = 10;

  const rooms = useMemo(() => {
    if (!allRooms) return [];
    const startIndex = 0;
    const endIndex = localPage * localPerPage;
    return allRooms.slice(startIndex, endIndex);
  }, [allRooms, localPage, localPerPage]);

  const hasMoreRooms = useMemo(() => {
    if (!allRooms) return false;
    return rooms.length < allRooms.length;
  }, [allRooms, rooms.length]);

  const handleLoadMore = () => {
    if (hasMoreRooms) {
      setLocalPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    setLocalPage(1);
  }, [allRooms]);
  // ENDREGION: Local Pagination

  return {
    allRooms,
    rooms,
    isLoadingRooms,
    handleGoToRoom,
    paginationInfo,
    handleSaveQuote,
    handleNextPage,
    handlePrevPage,
    hasNextPage: hasMoreRooms,
    handleLoadMore,
  };
};
export default useRooms;
